/* InternationalProduction.jsx — International enquiry page (full EN + CN i18n).
 *
 * 2026-02 rework: all copy now flows through `i18n.js` so the "中文" nav
 * toggle keeps the user on this page in the right language instead of
 * dropping them back on the home page.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import InternationalCollabBlock from "@/components/InternationalCollabBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useT } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://wa.me/60183628663";

export default function InternationalProduction() {
    const { t, lang } = useT();
    const location = useLocation();
    const [meta, setMeta] = useState(null);

    useEffect(() => {
        axios.get(`${API}/site-meta`).then(r => setMeta(r.data)).catch(() => setMeta({}));
    }, []);

    useEffect(() => {
        if (location.hash !== "#brief") return;
        const timer = setTimeout(() => {
            const el = document.getElementById("brief");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 150);
        return () => clearTimeout(timer);
    }, [location.hash]);

    // SEO head
    useEffect(() => {
        const canonical = lang === "cn"
            ? "https://slowcafe.my/international/cn"
            : "https://slowcafe.my/international";
        const title = lang === "cn"
            ? "国际合作 · Kaily F&B Visual Studio — 立足马来西亚承接跨国品牌项目"
            : "International F&B & Hospitality Film Production · Kaily F&B Visual Studio";
        const desc = lang === "cn"
            ? "马来西亚本地的影像创作者,长期承接新加坡、日本、台湾等区域品牌、酒店与餐饮项目。分享你的 brief,我们按项目提供定制报价。"
            : "Malaysia-based filmmaker available for international collaborations. Cinematic stories for food & hospitality brands. Share your brief for a tailored production plan.";
        const prevTitle = document.title;
        document.title = title;
        const ensure = (sel, make) => {
            let el = document.head.querySelector(sel);
            if (!el) { el = make(); document.head.appendChild(el); }
            return el;
        };
        const setMetaTag = (prop, name, content) => {
            const el = ensure(`meta[${prop}="${name}"]`, () => {
                const m = document.createElement("meta");
                m.setAttribute(prop, name);
                return m;
            });
            el.setAttribute("content", content);
        };
        setMetaTag("name", "description", desc);
        setMetaTag("name", "robots", "index, follow");
        setMetaTag("property", "og:type", "website");
        setMetaTag("property", "og:url", canonical);
        setMetaTag("property", "og:title", title);
        setMetaTag("property", "og:description", desc);
        const canon = ensure('link[rel="canonical"]', () => Object.assign(document.createElement("link"), { rel: "canonical" }));
        canon.setAttribute("href", canonical);
        return () => { document.title = prevTitle; };
    }, [lang]);

    const PROJECT_TYPES = [
        t("intl_page.project_types.brand"),
        t("intl_page.project_types.product"),
        t("intl_page.project_types.hospitality"),
        t("intl_page.project_types.travel"),
        t("intl_page.project_types.reels"),
        t("intl_page.project_types.photo"),
        t("intl_page.project_types.other"),
    ];

    const [form, setForm] = useState({
        name: "", business_name: "", email: "",
        country_city: "",
        project_type: PROJECT_TYPES[0],
        timeline: "",
        budget: "", reference_links: "",
        message: "",
    });

    // Keep the default project type synced with language (first option).
    useEffect(() => {
        setForm((p) => ({ ...p, project_type: PROJECT_TYPES[0] }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const up = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error(t("intl_page.form.err_missing"));
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/inquiries`, {
                name: form.name.trim(),
                business_name: form.business_name.trim() || null,
                email: form.email.trim(),
                services: [form.project_type],
                budget: form.budget.trim() || null,
                reference_links: form.reference_links.trim() || null,
                timeline: form.timeline.trim() || null,
                message: form.message.trim(),
                country: form.country_city.trim() || null,
                project_type: "international",
                source_page: lang === "cn" ? "/international/cn" : "/international",
                source_channel: "website_international",
            });
            setDone(true);
            toast.success(t("intl_page.form.ok_toast"));
        } catch (err) {
            toast.error(t("intl_page.form.err_toast"), { description: err?.response?.data?.detail || err.message });
        } finally {
            setSending(false);
        }
    };

    const inputCls = "mt-2 h-11 bg-transparent border border-border rounded-xl px-3 focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50";

    return (
        <div className="min-h-screen bg-cafe-cream text-cafe-espresso" data-testid="international-production-page">
            <SiteNav />
            <main>
                {/* ---------- Hero (shared olive block, now i18n'd) ---------- */}
                <div className="pt-16 md:pt-20">
                    <InternationalCollabBlock ctaHref="#brief" id="international-top" />
                </div>

                {/* ---------- Showcase reel (optional) ---------- */}
                {meta && (meta.international_showcase_reel || meta.international_showcase_image) && (
                    <section className="py-16 md:py-24 border-t border-cafe-espresso/10" data-testid="intl-showcase-section">
                        <div className="max-w-[1540px] mx-auto px-6 md:px-10 lg:px-14">
                            <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                                {t("intl_page.showcase.overline")}
                            </p>
                            <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso mb-10 text-balance" style={{ fontWeight: 500 }}>
                                {t("intl_page.showcase.title")}
                            </h2>
                            <div className="relative w-full bg-cafe-olive/15 overflow-hidden rounded-sm" style={{ aspectRatio: 16 / 9 }} data-testid="intl-showcase-media">
                                {meta.international_showcase_reel ? (
                                    <video
                                        src={meta.international_showcase_playback || meta.international_showcase_reel}
                                        poster={meta.international_showcase_image || undefined}
                                        muted autoPlay loop playsInline
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                ) : (
                                    <img
                                        src={meta.international_showcase_image}
                                        alt={meta.international_showcase_caption || "Showcase still"}
                                        className="absolute inset-0 w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                )}
                            </div>
                            {meta.international_showcase_caption && (
                                <p className="mt-4 text-[13px] text-cafe-espresso/70 font-display italic" data-testid="intl-showcase-caption-text">
                                    {meta.international_showcase_caption}
                                </p>
                            )}
                        </div>
                    </section>
                )}

                {/* ---------- How we work ---------- */}
                <section className="py-14 md:py-20 border-t border-cafe-espresso/10 bg-cafe-latte/40" data-testid="intl-how-section">
                    <div className="max-w-5xl mx-auto px-6 md:px-12">
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                            {t("intl_page.how.overline")}
                        </p>
                        <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso mb-10 text-balance" style={{ fontWeight: 500 }}>
                            {t("intl_page.how.title")}
                        </h2>
                        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
                            {[
                                ["filming", t("intl_page.how.filming.k"), t("intl_page.how.filming.v")],
                                ["brief", t("intl_page.how.brief.k"), t("intl_page.how.brief.v")],
                                ["prod", t("intl_page.how.prod.k"), t("intl_page.how.prod.v")],
                                ["deliv", t("intl_page.how.deliv.k"), t("intl_page.how.deliv.v")],
                                ["quote", t("intl_page.how.quote.k"), t("intl_page.how.quote.v")],
                                ["timeline", t("intl_page.how.timeline.k"), t("intl_page.how.timeline.v")],
                            ].map(([id, h, p]) => (
                                <div key={id} data-testid={`intl-how-${id}`}>
                                    <dt className="font-serif text-xl text-cafe-espresso mb-2">{h}</dt>
                                    <dd className="text-cafe-mocha leading-relaxed text-[15px]">{p}</dd>
                                </div>
                            ))}
                        </dl>
                        <p className="mt-10 text-[12px] text-cafe-espresso/55 leading-relaxed max-w-3xl">
                            {t("intl_page.how.footnote")}
                        </p>
                    </div>
                </section>

                {/* ---------- Enquiry form ---------- */}
                <section id="brief" className="py-16 md:py-24 border-t border-cafe-espresso/10 scroll-mt-24" data-testid="intl-brief-section">
                    <div className="max-w-3xl mx-auto px-6 md:px-12">
                        <p className="text-[10.5px] uppercase tracking-[0.32em] font-semibold text-cafe-espresso/75 mb-3">
                            {t("intl_page.brief.overline")}
                        </p>
                        <h2 className="font-serif text-3xl md:text-5xl text-cafe-espresso mb-4 text-balance" style={{ fontWeight: 500 }} data-testid="intl-section-brief">
                            {t("intl_page.brief.title")}
                        </h2>
                        <p className="text-cafe-mocha mb-10 max-w-xl">
                            {t("intl_page.brief.body")}
                        </p>

                        {done ? (
                            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-6 text-emerald-900" data-testid="intl-form-done">
                                <div className="font-serif text-2xl mb-2">{t("intl_page.form.done.title")}</div>
                                <p>
                                    {t("intl_page.form.done.body_1")} <strong>kailygan.my@gmail.com</strong> {t("intl_page.form.done.body_2")}
                                </p>
                            </div>
                        ) : (
                            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="intl-form">
                                <div><Label>{t("intl_page.form.name")} <span className="text-cafe-olive">*</span></Label><Input data-testid="intl-name" value={form.name} onChange={up("name")} className={inputCls} /></div>
                                <div><Label>{t("intl_page.form.email")} <span className="text-cafe-olive">*</span></Label><Input type="email" data-testid="intl-email" value={form.email} onChange={up("email")} className={inputCls} /></div>
                                <div><Label>{t("intl_page.form.brand")}</Label><Input data-testid="intl-brand" value={form.business_name} onChange={up("business_name")} className={inputCls} /></div>
                                <div><Label>{t("intl_page.form.country")}</Label><Input data-testid="intl-country-city" value={form.country_city} onChange={up("country_city")} placeholder={t("intl_page.form.country_ph")} className={inputCls} /></div>
                                <div>
                                    <Label>{t("intl_page.form.project_type")}</Label>
                                    <select data-testid="intl-project-type" value={form.project_type} onChange={up("project_type")} className={`${inputCls} appearance-none`}>
                                        {PROJECT_TYPES.map((c) => <option key={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div><Label>{t("intl_page.form.timeline")}</Label><Input data-testid="intl-timeline" value={form.timeline} onChange={up("timeline")} placeholder={t("intl_page.form.timeline_ph")} className={inputCls} /></div>
                                <div className="md:col-span-2"><Label>{t("intl_page.form.budget")}</Label><Input data-testid="intl-budget" value={form.budget} onChange={up("budget")} placeholder={t("intl_page.form.budget_ph")} className={inputCls} /></div>
                                <div className="md:col-span-2">
                                    <Label>{t("intl_page.form.refs")}</Label>
                                    <Textarea data-testid="intl-reference-links" rows={2} value={form.reference_links} onChange={up("reference_links")} placeholder={"https://www.instagram.com/p/...\nhttps://vimeo.com/..."} className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50" />
                                </div>
                                <div className="md:col-span-2">
                                    <Label>{t("intl_page.form.brief")} <span className="text-cafe-olive">*</span></Label>
                                    <Textarea data-testid="intl-message" rows={6} value={form.message} onChange={up("message")} placeholder={t("intl_page.form.brief_ph")} className="mt-2 bg-transparent border border-border rounded-xl focus-visible:ring-0 focus-visible:border-cafe-olive text-cafe-espresso placeholder:text-cafe-mocha/50" />
                                </div>
                                <div className="md:col-span-2 flex flex-wrap gap-3 items-center">
                                    <Button type="submit" disabled={sending} data-testid="intl-submit" className="rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream h-12 px-8 font-semibold">
                                        {sending ? t("intl_page.form.submitting") : t("intl_page.form.submit")}
                                    </Button>
                                    <a
                                        href={WHATSAPP_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid="intl-wa"
                                        className="inline-flex items-center h-12 px-6 rounded-full border border-cafe-espresso text-cafe-espresso text-[13.5px] font-medium hover:bg-cafe-espresso hover:text-cafe-cream transition-colors"
                                    >
                                        {t("intl_page.cta_wa")}
                                    </a>
                                </div>
                                <div className="md:col-span-2">
                                    <p className="text-xs text-cafe-mocha/70">
                                        {t("intl_page.form.footnote")}
                                    </p>
                                </div>
                            </form>
                        )}
                    </div>
                </section>
            </main>
            <SiteFooter />
        </div>
    );
}
